import type {ReactElement} from 'react';
import {useCallback, useEffect, useState} from 'react';
import {ColorPickerPopup} from '@Components/color-picker-popup';
import type {DropdownProps} from '@Components/dropdown-v2';
import {Dropdown} from '@Components/dropdown-v2';
import {noop} from '@Utils/general.util';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {updateDoCloseDropdown} from '@Components/email-maker-color-picker-dropdown/email-maker-color-picker-dropdown-reducer';

export interface EmailMakerColorPickerDropdownProps extends Partial<DropdownProps> {
  initialColor: string;
  onColorChange: (color: string) => void;
  onColorChangeEnd: (color: string) => void;
  dropdownSelectorClasses?: string;
  ignoreSharedRteColor?: boolean;
}

export function EmailMakerColorPickerDropdown({
  initialColor,
  onColorChange,
  onColorChangeEnd,
  dropdownSelectorClasses = '',
  ignoreSharedRteColor = false,
  onClose = noop,
  onClick = noop,
  ...props
}: EmailMakerColorPickerDropdownProps): ReactElement {
  const colorForColorPicker = useAppSelector((state) => {
    return state.emailMakerColorPickerDropdown.colorForColorPicker;
  });

  const doCloseDropdown = useAppSelector((state) => {
    return state.emailMakerColorPickerDropdown.doCloseDropdown;
  });

  const [color, setColor] = useState(initialColor);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dispatch = useAppDispatch();

  const triggerDropDownClose = useCallback((): void => {
    setIsDropdownOpen(false);
    onClose();
    dispatch(updateDoCloseDropdown(false));
  }, [onClose, dispatch]);

  // Ignore the empty default of the shared RTE color global so it never clobbers the prop-driven swatch on mount.
  useEffect(() => {
    if (!ignoreSharedRteColor && colorForColorPicker) {
      setColor((prev) => (colorForColorPicker !== prev ? colorForColorPicker : prev));
    }

    if (doCloseDropdown) {
      triggerDropDownClose();
    }
  }, [colorForColorPicker, doCloseDropdown, triggerDropDownClose, ignoreSharedRteColor]);

  // Skipped while the picker is open so an in-progress drag isn't reverted to the last-committed value mid-gesture.
  useEffect(() => {
    if (!isDropdownOpen) {
      setColor(initialColor);
    }
  }, [initialColor, isDropdownOpen]);

  const getSelectorForDropdown = (): ReactElement => {
    return <div className={dropdownSelectorClasses} style={{backgroundColor: color}} />;
  };

  const handleColorPickerColorChange = (newColor: string): void => {
    setColor(newColor);
    onColorChange(newColor);
  };

  const handleColorPickerColorChangeEnd = (newColor: string): void => {
    setColor(newColor);
    onColorChangeEnd(newColor);
  };

  const getColorPickerPopup = (): ReactElement => {
    return <ColorPickerPopup hideEyeDropperPicker={false} color={color} onChange={handleColorPickerColorChange} onChangeEnded={handleColorPickerColorChangeEnd} isAlphaDisabled />;
  };

  return (
    <Dropdown
      id="email-maker-text-colorpicker-dropdown"
      key="email-maker-text-colorpicker-dropdown"
      open={isDropdownOpen}
      popup={getColorPickerPopup()}
      selector={getSelectorForDropdown()}
      onClick={() => {
        onClick();
        setIsDropdownOpen(true);
      }}
      onClose={() => {
        onClose();
        setIsDropdownOpen(false);
      }}
      popUpHasCustomWidth
      popUpHasCustomHeight
      shouldFlip
      doesCloseOnOuterClick
      doCloseOnInnerClick={false}
      {...props}
    />
  );
}
